import { useState, useEffect } from 'react'

const checklistGroups = [
  {
    title: 'Site & Cardápio',
    items: [
      'Site PWA do restaurante',
      'Domínio personalizado',
      'My Pages — editor visual completo',
      'Cores, fontes e identidade visual',
      'Upload de fotos e banner',
      'Cardápio digital com categorias',
      'Fotos e descrição por item',
      'Complementos e adicionais',
      'Múltiplos grupos de cardápio',
      'Busca em tempo real no cardápio',
      'Publicação instantânea',
    ],
  },
  {
    title: 'Pedidos & Pagamentos',
    items: [
      'Pedidos de mesa (dine-in)',
      'Pedidos de delivery',
      'Pedidos de retirada (pickup)',
      'Painel de pedidos em tempo real',
      'Status customizáveis por fluxo',
      'Controle de pagamento',
      'Pagamento online via Pix',
      'Impressão para cozinha e bar',
      'Modo despacho (gestão de entregadores)',
      'Reservas (em breve)',
    ],
  },
  {
    title: 'Equipe & Crescimento',
    items: [
      'Mapa de mesas em tempo real',
      'QR code único por mesa',
      'Abertura e fechamento de sessões',
      'Até 15 membros da equipe',
      'Perfis: Dono, Gerente, Operador',
      'Cupons de desconto',
      'Cashback para clientes',
      'Analytics e relatórios',
      'Programa de fidelidade',
      'Suporte prioritário via chat',
    ],
  },
]

function Check({ featured }) {
  return (
    <div
      className="w-[18px] h-[18px] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
      style={{ background: featured ? 'rgba(255,255,255,0.15)' : '#141410' }}
    >
      <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
        <path d="M1.5 4.5l2 2L7.5 2.5" stroke="white" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </div>
  )
}

// Deadline: 30 days from May 14, 2026
const DEADLINE = new Date('2026-06-13T23:59:59')

function computeTimeLeft(deadline) {
  const diff = deadline - Date.now()
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true }
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
    expired: false,
  }
}

function useCountdown(deadline) {
  const [timeLeft, setTimeLeft] = useState(() => computeTimeLeft(deadline))

  useEffect(() => {
    const id = setInterval(() => setTimeLeft(computeTimeLeft(deadline)), 1000)
    return () => clearInterval(id)
  }, [deadline])

  return timeLeft
}

function CountdownUnit({ value, label }) {
  return (
    <div className="flex flex-col items-center">
      <div
        className="text-[22px] lg:text-[28px] font-black tabular-nums leading-none text-white px-3 py-2 rounded-xl"
        style={{ background: 'rgba(255,255,255,0.08)', minWidth: '52px', textAlign: 'center' }}
      >
        {String(value).padStart(2, '0')}
      </div>
      <div className="text-[10px] font-bold uppercase tracking-[1px] text-white/35 mt-1.5">{label}</div>
    </div>
  )
}

export default function Pricing() {
  const { days, hours, minutes, seconds, expired } = useCountdown(DEADLINE)

  return (
    <section className="bg-white px-5 py-16 sm:px-8 sm:py-20 lg:px-16 lg:py-24 scroll-mt-24" id="precos">
      <div className="max-w-[1140px] mx-auto">

        <div className="text-[11px] font-bold uppercase tracking-[2px] text-neutral-400 mb-3">
          Preços
        </div>
        <h2 className="text-[30px] sm:text-[38px] lg:text-[50px] font-black tracking-tight leading-[1.08] mb-4">
          Simples e sem surpresas.
        </h2>
        <p className="text-[15px] lg:text-[16px] text-neutral-500 leading-[1.75] max-w-[520px]">
          Sem taxa por pedido. Sem comissão. O que o cliente paga, é seu.
        </p>

        {/* ── Early price callout ── */}
        <div
          className="rounded-[20px] lg:rounded-3xl p-8 lg:p-[52px] flex flex-col lg:flex-row gap-7 lg:gap-12 items-start lg:items-center mt-10 lg:mt-14 mb-10 lg:mb-14 relative overflow-hidden"
          style={{ background: '#0E0E0C' }}
        >
          {/* Glow */}
          <div
            className="absolute top-[-80px] right-[-80px] w-[280px] h-[280px] rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.03) 0%, transparent 70%)' }}
          />

          {/* Left */}
          <div className="flex-1 relative z-10">
            <div className="inline-flex items-center gap-2 bg-white/8 border border-white/10 rounded-full px-3.5 py-1.5 text-[11px] font-bold text-white/60 uppercase tracking-[1px] mb-4">
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full pulse-dot" />
              Oferta por tempo limitado
            </div>
            <div className="text-[24px] lg:text-[38px] font-black tracking-tight leading-[1.15] text-white mb-3">
              {expired
                ? <>Oferta encerrada.<br className="hidden lg:block" />Plano Pro por R$199/mês.</>
                : <>Trave R$149/mês agora<br className="hidden lg:block" />e pague isso para sempre.</>
              }
            </div>
            <p className="text-[14px] lg:text-[15px] text-white/50 leading-[1.7] max-w-[420px]">
              {expired
                ? 'Esta oferta encerrou. O plano Pro agora custa R$199/mês, sujeito a alterações.'
                : 'Por tempo limitado, quem entrar agora trava R$149/mês para sempre. Após essa oferta, o plano Pro passará a custar R$199/mês — sujeito a alterações. Quem entrar agora não paga mais do que isso, nunca.'
              }
            </p>
            <p className="text-[12px] text-white/25 mt-3">Cancele quando quiser. Sem contrato. Sem fidelidade.</p>
            <p className="text-[11px] text-white/20 mt-2">
              Preço travado para o plano atual. Pix via Woovi, máx. R$1/transação.{' '}
              <a href="/termos" className="underline text-white/35 hover:text-white/55 transition-colors">Ver termos completos →</a>
            </p>

            {/* Countdown */}
            {!expired && (
              <div className="mt-6">
                <p className="text-[11px] font-bold uppercase tracking-[1.5px] text-white/30 mb-3">
                  Oferta encerra em
                </p>
                <div className="flex items-end gap-3">
                  <CountdownUnit value={days} label="dias" />
                  <span className="text-white/30 text-[20px] font-black mb-3">:</span>
                  <CountdownUnit value={hours} label="horas" />
                  <span className="text-white/30 text-[20px] font-black mb-3">:</span>
                  <CountdownUnit value={minutes} label="min" />
                  <span className="text-white/30 text-[20px] font-black mb-3">:</span>
                  <CountdownUnit value={seconds} label="seg" />
                </div>
              </div>
            )}
          </div>

          {/* Right: price card */}
          <div className="relative z-10 w-full lg:w-auto lg:flex-shrink-0 lg:min-w-[220px]">
            <div className="bg-white rounded-2xl p-6 lg:p-8">
              <div className="text-[11px] font-bold uppercase tracking-[1px] text-neutral-400 mb-1.5">
                Plano Pro · mensal
              </div>

              {/* Strikethrough future price */}
              {!expired && (
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[13px] text-neutral-400 line-through">R$199/mês</span>
                  <span
                    className="text-[10px] font-bold uppercase tracking-[0.5px] text-white px-2 py-0.5 rounded-full"
                    style={{ background: '#16a34a' }}
                  >
                    Preço garantido
                  </span>
                </div>
              )}

              <div className="flex items-start leading-none mb-1">
                <sup className="text-[20px] font-black mt-5 tracking-tight">R$</sup>
                <span className="text-[56px] font-black tracking-[-3px]">
                  {expired ? '199' : '149'}
                </span>
              </div>
              <div className="text-[13px] text-neutral-400 mb-5">por mês · sem contrato</div>
              <a
                href="#testar-gratis"
                className="flex items-center justify-center bg-[#141410] text-white no-underline px-6 py-3.5 rounded-full text-[14px] font-bold hover:bg-[#2c2c28] transition-colors"
              >
                Começar 30 dias grátis →
              </a>
              <p className="text-[11px] text-neutral-400 text-center mt-3">Sem cartão de crédito</p>
            </div>
          </div>
        </div>

        {/* ── What's included checklist ── */}
        <div
          className="border rounded-[18px] lg:rounded-3xl p-7 lg:p-12"
          style={{ background: '#FAFAF8', borderColor: '#ebebeb' }}
        >
          <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between mb-7 lg:mb-9 gap-1">
            <div className="text-[17px] lg:text-[18px] font-extrabold tracking-tight">
              O que está incluso no plano Pro
            </div>
            <div className="text-[13px] text-neutral-400">Tudo ativo desde o primeiro dia.</div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-0">
            {checklistGroups.map((group, i) => (
              <div
                key={group.title}
                className={`
                  ${i === 0 ? 'lg:pr-8 lg:border-r lg:border-neutral-200' : ''}
                  ${i === 1 ? 'lg:px-8 lg:border-r lg:border-neutral-200' : ''}
                  ${i === 2 ? 'lg:pl-8' : ''}
                `}
              >
                <div className="text-[10px] font-bold uppercase tracking-[1.5px] text-neutral-400 mb-4 pb-2.5 border-b border-neutral-200">
                  {group.title}
                </div>
                <div className="flex flex-col gap-3">
                  {group.items.map((item) => (
                    <div key={item} className="flex items-start gap-2.5 text-[13px] text-black leading-[1.4]">
                      <Check />
                      <span className={item.includes('em breve') ? 'text-neutral-400' : ''}>
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  )
}
